import classNames from 'classnames'

import ProductReview from '@src/quince-site-common/src/domains/ProductReview'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { getReviewImageAltText } from '@src/quince-site-pdp/src/atoms/reviewPopUp/utils'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import Swiper from '../../custom-swiper'
import Image from '../../image'
import { DIRECTION } from '../../swiper/Swiper'

// style
import { NUMBER_VISIBLE_SLIDES } from './productReviewGallerySlider.constants'
import styles from './productReviewGallerySlider.module.scss'
import stylesV2 from './productReviewGallerySliderV2.module.scss'

export interface ProductReviewGallerySliderProps {
  heading: string
  productReviews: ProductReview[]
  onReviewImageClick: (reviewId: number, imageIndex: number) => void
  productTitle?: string
  newPdpv2?: boolean
  isHighlightSection?: boolean
  productProperties: ProductProperties
  isReviewSystemV3Enabled?: boolean
}

export const ProductReviewGallerySlider = (props: ProductReviewGallerySliderProps) => {
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const finalStyles = props.newPdpv2 ? stylesV2 : styles
  const getIsVisibleTrackParams = () => {
    return {
      action: 'pdp_reviews_gallery',
      value: true,
      context: {
        photo_count: NUMBER_VISIBLE_SLIDES,
      },
    }
  }

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  return (
    <div className={finalStyles.gallaryWrap}>
      <div
        ref={trackIsVisibleRef}
        className={classNames(
          finalStyles.galleryInner,
          props.isReviewSystemV3Enabled && !props.newPdpv2 && finalStyles.galleryInner__adjustedSpacing,
          { [finalStyles.galleryInner__highlightSection ?? '']: props.isHighlightSection },
        )}
      >
        {props.heading && (
          <h2
            data-testid="review-section-gallery-heading"
            className={classNames(
              finalStyles.heading,
              props.newPdpv2 && finalStyles.heading__newPdpv2,
              props.isHighlightSection && finalStyles.heading__withoutPaddingLeft,
            )}
          >
            {props.heading}
          </h2>
        )}
        <Swiper
          id={'product-review-gallery-slider'}
          showNavigator={isDesktop}
          className={finalStyles.gallerySliderWrap}
          virtualOptions={{ enabled: true }}
          prevIconClass={finalStyles.prevArrowIcon}
          nextIconClass={finalStyles.nextArrowIcon}
          numberOfVisibleSlides={NUMBER_VISIBLE_SLIDES}
          wrapperClass={classNames(
            !props.isHighlightSection && finalStyles.gallaryWrap_swiperWrapperClass__adjustedPaddingLeft,
            !props.isHighlightSection &&
              props.newPdpv2 &&
              finalStyles.gallaryWrap_swiperWrapperClass__adjustedPaddingLeft__newPdpv2,
          )}
          arrowOnClick={({ direction }) => {
            trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, getIsVisibleTrackParams())
            trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
              action: direction === DIRECTION.NEXT ? 'click_carousel_right' : 'click_carousel_left',
              value: true,
              context: {
                // eslint-disable-next-line i18next/no-literal-string
                contentContainerDisplayName: 'GALLERY',
                // eslint-disable-next-line i18next/no-literal-string
                contentContainerName: 'Single Swimlane',
                // eslint-disable-next-line i18next/no-literal-string
                contentContainerType: 'Reviews Gallery',
                productProperties: props.productProperties,
              },
            })
          }}
        >
          {props.productReviews.map((review: ProductReview) => {
            return review.imagesFileUrls?.map((fileUrl: string, imageIndex) => {
              return (
                <div
                  key={fileUrl}
                  onClick={() => {
                    props.onReviewImageClick(review.id, imageIndex)
                    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
                      action: 'click_reviews_gallery',
                      value: true,
                      context: {
                        productProperties: props.productProperties,
                        review_id: review.id,
                        imageUrl: fileUrl,
                      },
                    })
                  }}
                  className={finalStyles.slideItem}
                >
                  <Image
                    alt={getReviewImageAltText({ reviewTitle: review.title, productTitle: props.productTitle })}
                    title={review.title}
                    url={fileUrl}
                    key={fileUrl}
                    className={classNames(finalStyles.slideImage, {
                      [finalStyles.slideImage_highlight ?? '']: props.isHighlightSection,
                    })}
                    fillWithBlackBackground={false}
                    // @i18n-ignore
                    loading={'lazy'}
                  />
                </div>
              )
            })
          })}
        </Swiper>
      </div>
    </div>
  )
}

export default ProductReviewGallerySlider
